import React, { createContext, useContext, useReducer, useCallback } from 'react';
import {
  INTERVIEW_PHASES,
  CONNECTION_STATES,
  MEDIA_STATES,
  LANGUAGES,
  STORAGE_KEYS
} from '@shared/utils/constants';

// Initial State
const initialState = {
  // Auth
  isAuthenticated: false,
  isAuthInitialized: false, // True after checking sessionStorage
  user: null,
  sessionId: null,
  accessToken: null,

  // Interview Phase
  phase: INTERVIEW_PHASES.LOGIN,
  
  // Media
  mediaState: MEDIA_STATES.IDLE,
  cameraEnabled: true,
  microphoneEnabled: true,
  mediaStream: null,
  
  // Recording
  isRecording: false,
  recordingStartTime: null,
  recordingDuration: 0,
  videoChunks: [],
  
  // Connection
  connectionState: CONNECTION_STATES.DISCONNECTED,
  networkQuality: 'unknown',
  
  // Interview Data
  interviewType: 'Technical Interview',
  interviewer: {
    name: 'Sarah',
    avatar: null,
  },
  transcript: [],
  
  // UI State
  language: LANGUAGES.AR,
  isRTL: true,
  
  // Errors
  error: null,
  errorHistory: [],
  
  // Timers
  interviewStartTime: null,
  interviewDuration: 0,
};

// Action Types
const ACTION_TYPES = {
  // Auth
  SET_AUTH: 'SET_AUTH',
  SET_AUTH_INITIALIZED: 'SET_AUTH_INITIALIZED',
  LOGOUT: 'LOGOUT',

  // Phase
  SET_PHASE: 'SET_PHASE',
  
  // Media
  SET_MEDIA_STATE: 'SET_MEDIA_STATE',
  SET_MEDIA_STREAM: 'SET_MEDIA_STREAM',
  TOGGLE_CAMERA: 'TOGGLE_CAMERA',
  TOGGLE_MICROPHONE: 'TOGGLE_MICROPHONE',
  
  // Recording
  START_RECORDING: 'START_RECORDING',
  STOP_RECORDING: 'STOP_RECORDING',
  ADD_VIDEO_CHUNK: 'ADD_VIDEO_CHUNK',
  CLEAR_VIDEO_CHUNKS: 'CLEAR_VIDEO_CHUNKS',
  UPDATE_RECORDING_DURATION: 'UPDATE_RECORDING_DURATION',
  
  // Connection
  SET_CONNECTION_STATE: 'SET_CONNECTION_STATE',
  SET_NETWORK_QUALITY: 'SET_NETWORK_QUALITY',
  
  // Interview
  SET_INTERVIEW_DATA: 'SET_INTERVIEW_DATA',
  ADD_TRANSCRIPT_MESSAGE: 'ADD_TRANSCRIPT_MESSAGE',
  UPDATE_INTERVIEW_DURATION: 'UPDATE_INTERVIEW_DURATION',
  
  // UI
  SET_LANGUAGE: 'SET_LANGUAGE',
  
  // Errors
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR',
  
  // Reset
  RESET_STATE: 'RESET_STATE',
};

// Reducer
const interviewReducer = (state, action) => {
  switch (action.type) {
    case ACTION_TYPES.SET_AUTH:
      // Persist auth data to sessionStorage (survives refresh, clears on tab close)
      sessionStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, action.payload.accessToken || '');
      sessionStorage.setItem(STORAGE_KEYS.SESSION_ID, action.payload.sessionId || '');
      sessionStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(action.payload.user || {}));

      return {
        ...state,
        isAuthenticated: true,
        user: action.payload.user,
        sessionId: action.payload.sessionId,
        accessToken: action.payload.accessToken,
      };
      
    case ACTION_TYPES.SET_AUTH_INITIALIZED:
      return {
        ...state,
        isAuthInitialized: true,
      };

    case ACTION_TYPES.LOGOUT:
      sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
      sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
      sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
      sessionStorage.removeItem(STORAGE_KEYS.CURRENT_PHASE);
      return {
        ...initialState,
        isAuthInitialized: true, // Keep initialized after logout
        language: state.language,
        isRTL: state.isRTL,
      };
      
    case ACTION_TYPES.SET_PHASE:
      // Persist phase to sessionStorage (survives refresh)
      sessionStorage.setItem(STORAGE_KEYS.CURRENT_PHASE, action.payload);
      return {
        ...state,
        phase: action.payload,
      };
      
    case ACTION_TYPES.SET_MEDIA_STATE:
      return {
        ...state,
        mediaState: action.payload,
      };
      
    case ACTION_TYPES.SET_MEDIA_STREAM:
      return {
        ...state,
        mediaStream: action.payload,
        mediaState: action.payload ? MEDIA_STATES.ACTIVE : MEDIA_STATES.IDLE,
      };
      
    case ACTION_TYPES.TOGGLE_CAMERA:
      return {
        ...state,
        cameraEnabled: !state.cameraEnabled,
      };
      
    case ACTION_TYPES.TOGGLE_MICROPHONE:
      return {
        ...state,
        microphoneEnabled: !state.microphoneEnabled,
      };
      
    case ACTION_TYPES.START_RECORDING:
      return {
        ...state,
        isRecording: true,
        recordingStartTime: Date.now(),
        recordingDuration: 0,
      };
      
    case ACTION_TYPES.STOP_RECORDING:
      return {
        ...state,
        isRecording: false,
      };
      
    case ACTION_TYPES.ADD_VIDEO_CHUNK:
      return {
        ...state,
        videoChunks: [...state.videoChunks, action.payload],
      };
      
    case ACTION_TYPES.CLEAR_VIDEO_CHUNKS:
      return {
        ...state,
        videoChunks: [],
      };
      
    case ACTION_TYPES.UPDATE_RECORDING_DURATION:
      return {
        ...state,
        recordingDuration: action.payload,
      };
      
    case ACTION_TYPES.SET_CONNECTION_STATE:
      return {
        ...state,
        connectionState: action.payload,
      };
      
    case ACTION_TYPES.SET_NETWORK_QUALITY:
      return {
        ...state,
        networkQuality: action.payload,
      };
      
    case ACTION_TYPES.SET_INTERVIEW_DATA:
      return {
        ...state,
        ...action.payload,
      };
      
    case ACTION_TYPES.ADD_TRANSCRIPT_MESSAGE:
      return {
        ...state,
        transcript: [...state.transcript, action.payload],
      };
      
    case ACTION_TYPES.UPDATE_INTERVIEW_DURATION:
      return {
        ...state,
        interviewDuration: action.payload,
      };
      
    case ACTION_TYPES.SET_LANGUAGE:
      const isRTL = action.payload === LANGUAGES.AR;
      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = action.payload;
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, action.payload);
      return {
        ...state,
        language: action.payload,
        isRTL,
      };
      
    case ACTION_TYPES.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        errorHistory: [...state.errorHistory, { ...action.payload, timestamp: Date.now() }],
      };
      
    case ACTION_TYPES.CLEAR_ERROR:
      return {
        ...state,
        error: null,
      };
      
    case ACTION_TYPES.RESET_STATE:
      return {
        ...initialState,
        language: state.language,
        isRTL: state.isRTL,
      };
      
    default:
      return state;
  }
};

// Context
const InterviewContext = createContext(null);

// Provider Component
export const InterviewProvider = ({ children }) => {
  const [state, dispatch] = useReducer(interviewReducer, initialState);

  // Initialize auth state and language from localStorage on mount
  React.useEffect(() => {
    // Initialize language - Default to Arabic
    const savedLanguage = localStorage.getItem(STORAGE_KEYS.LANGUAGE);

    // If no saved language or if we want to ensure Arabic is default, set Arabic
    if (!savedLanguage) {
      dispatch({ type: ACTION_TYPES.SET_LANGUAGE, payload: LANGUAGES.AR });
    } else if (savedLanguage === LANGUAGES.EN || savedLanguage === LANGUAGES.AR) {
      dispatch({ type: ACTION_TYPES.SET_LANGUAGE, payload: savedLanguage });
    } else {
      // Invalid saved language, default to Arabic
      dispatch({ type: ACTION_TYPES.SET_LANGUAGE, payload: LANGUAGES.AR });
    }

    // Initialize auth from sessionStorage (survives page refresh)
    const token = sessionStorage.getItem(STORAGE_KEYS.AUTH_TOKEN);
    const sessionId = sessionStorage.getItem(STORAGE_KEYS.SESSION_ID);
    const userData = sessionStorage.getItem(STORAGE_KEYS.USER_DATA);
    const savedPhase = sessionStorage.getItem(STORAGE_KEYS.CURRENT_PHASE);

    if (token && userData) {
      try {
        const user = JSON.parse(userData);
        dispatch({
          type: ACTION_TYPES.SET_AUTH,
          payload: {
            user,
            sessionId: sessionId || '',
            accessToken: token
          }
        });

        // Restore phase if saved (but only if authenticated)
        // Keep INTERVIEW phase - will reconnect to same room (like connection loss)
        if (savedPhase && Object.values(INTERVIEW_PHASES).includes(savedPhase)) {
          dispatch({ type: ACTION_TYPES.SET_PHASE, payload: savedPhase });
          console.log('Phase restored:', savedPhase);
        } else {
          // Default to PROCEED phase after login restoration
          dispatch({ type: ACTION_TYPES.SET_PHASE, payload: INTERVIEW_PHASES.PROCEED });
        }

        console.log('Auth restored from session:', { user: user?.name, phase: savedPhase || INTERVIEW_PHASES.PROCEED });
      } catch (error) {
        console.error('Failed to restore auth state:', error);
        // Clear invalid data
        sessionStorage.removeItem(STORAGE_KEYS.AUTH_TOKEN);
        sessionStorage.removeItem(STORAGE_KEYS.SESSION_ID);
        sessionStorage.removeItem(STORAGE_KEYS.USER_DATA);
        sessionStorage.removeItem(STORAGE_KEYS.CURRENT_PHASE);
      }
    }

    // Mark auth initialization as complete
    dispatch({ type: ACTION_TYPES.SET_AUTH_INITIALIZED });
  }, []);

  // Auth Actions
  const login = useCallback((userData) => {
    dispatch({ type: ACTION_TYPES.SET_AUTH, payload: userData });
  }, []);
  
  const logout = useCallback(() => {
    dispatch({ type: ACTION_TYPES.LOGOUT });
  }, []);
  
  // Phase Actions
  const setPhase = useCallback((phase) => {
    dispatch({ type: ACTION_TYPES.SET_PHASE, payload: phase });
  }, []);
  
  // Media Actions
  const setMediaState = useCallback((state) => {
    dispatch({ type: ACTION_TYPES.SET_MEDIA_STATE, payload: state });
  }, []);
  
  const setMediaStream = useCallback((stream) => {
    dispatch({ type: ACTION_TYPES.SET_MEDIA_STREAM, payload: stream });
  }, []);
  
  const toggleCamera = useCallback(() => {
    dispatch({ type: ACTION_TYPES.TOGGLE_CAMERA });
  }, []);
  
  const toggleMicrophone = useCallback(() => {
    dispatch({ type: ACTION_TYPES.TOGGLE_MICROPHONE });
  }, []);
  
  // Recording Actions
  const startRecording = useCallback(() => {
    dispatch({ type: ACTION_TYPES.START_RECORDING });
  }, []);
  
  const stopRecording = useCallback(() => {
    dispatch({ type: ACTION_TYPES.STOP_RECORDING });
  }, []);
  
  const addVideoChunk = useCallback((chunk) => {
    dispatch({ type: ACTION_TYPES.ADD_VIDEO_CHUNK, payload: chunk });
  }, []);
  
  const clearVideoChunks = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_VIDEO_CHUNKS });
  }, []);
  
  const updateRecordingDuration = useCallback((duration) => {
    dispatch({ type: ACTION_TYPES.UPDATE_RECORDING_DURATION, payload: duration });
  }, []);
  
  // Connection Actions
  const setConnectionState = useCallback((connectionState) => {
    dispatch({ type: ACTION_TYPES.SET_CONNECTION_STATE, payload: connectionState });
  }, []);
  
  const setNetworkQuality = useCallback((quality) => {
    dispatch({ type: ACTION_TYPES.SET_NETWORK_QUALITY, payload: quality });
  }, []);
  
  // Interview Actions
  const setInterviewData = useCallback((data) => {
    dispatch({ type: ACTION_TYPES.SET_INTERVIEW_DATA, payload: data });
  }, []);
  
  const addTranscriptMessage = useCallback((message) => {
    dispatch({ type: ACTION_TYPES.ADD_TRANSCRIPT_MESSAGE, payload: message });
  }, []);
  
  const updateInterviewDuration = useCallback((duration) => {
    dispatch({ type: ACTION_TYPES.UPDATE_INTERVIEW_DURATION, payload: duration });
  }, []);
  
  // UI Actions
  const setLanguage = useCallback((language) => {
    dispatch({ type: ACTION_TYPES.SET_LANGUAGE, payload: language });
  }, []);
  
  // Error Actions
  const setError = useCallback((error) => {
    dispatch({ type: ACTION_TYPES.SET_ERROR, payload: error });
  }, []);
  
  const clearError = useCallback(() => {
    dispatch({ type: ACTION_TYPES.CLEAR_ERROR });
  }, []);
  
  // Reset
  const resetState = useCallback(() => {
    dispatch({ type: ACTION_TYPES.RESET_STATE });
  }, []);
  
  const value = {
    state,
    actions: {
      login,
      logout,
      setPhase,
      setMediaState,
      setMediaStream,
      toggleCamera,
      toggleMicrophone,
      startRecording,
      stopRecording,
      addVideoChunk,
      clearVideoChunks,
      updateRecordingDuration,
      setConnectionState,
      setNetworkQuality,
      setInterviewData,
      addTranscriptMessage,
      updateInterviewDuration,
      setLanguage,
      setError,
      clearError,
      resetState,
    },
  };
  
  return (
    <InterviewContext.Provider value={value}>
      {children}
    </InterviewContext.Provider>
  );
};

// Hook
export const useInterview = () => {
  const context = useContext(InterviewContext);
  if (!context) {
    throw new Error('useInterview must be used within an InterviewProvider');
  }
  return context;
};

export default InterviewContext;
